import { CommonModule } from "@angular/common";
import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  forwardRef,
} from "@angular/core";
import {
  FormsModule,
  NG_VALUE_ACCESSOR,
  ControlValueAccessor,
} from "@angular/forms";
import {
  CATEGORY_API_URL,
  GLOBAL_SETTINGS_API_URL,
  LISTING_API_URL,
  USER_API_URL,
} from "@app/data/const";
import { Category, Listing } from "@app/data/listing";
import { RemoteDataService } from "@nowinstore/nowinstore-core-ui/components";
import { MessageService, TreeNode } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { InputTextModule } from "primeng/inputtext";
import * as _ from "lodash";
import { CheckboxModule } from "primeng/checkbox";
import { GlobalSettings } from "@app/services/global-settings.service";
import { User } from "@app/data/user";
import { TreeModule } from "primeng/tree";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { OverlayPanelModule } from "primeng/overlaypanel";
import { SkeletonModule } from "primeng/skeleton";
import { ThumbnalizePipe } from "@app/pipe/thumbnalize.pipe";

@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    InputTextModule,
    ButtonModule,
    CheckboxModule,
    TreeModule,
    ProgressSpinnerModule,
    ThumbnalizePipe,
    OverlayPanelModule,
    SkeletonModule,
  ],
  selector: "nis-listings-picker",
  templateUrl: "./listings-picker.component.html",
  styles: [
    `
      :host
        ::ng-deep
        .p-tree
        .p-tree-container
        .p-treenode
        .p-treenode-content.p-highlight {
        color: #00a5cf;
        background: transparent;
      }

      :host ::ng-deep .p-checkbox .p-checkbox-box.p-highlight {
        border-color: #00a5cf;
        background: #00a5cf;
      }

      :host
        ::ng-deep
        .p-tree
        .p-tree-container
        .p-treenode
        .p-treenode-content.p-treenode-selectable:not(.p-highlight):hover {
        color: #004a61;
        background: transparent;
      }

      :host
        ::ng-deep
        .p-tree
        .p-tree-container
        .p-treenode
        .p-treenode-content
        .p-tree-toggler {
        color: #798da9;
      }
      :host
        ::ng-deep
        .p-tree
        .p-tree-container
        .p-treenode
        .p-treenode-content
        .p-tree-toggler:enabled:hover {
        background: #e9ecef;
      }
      :host
        ::ng-deep
        .p-tree
        .p-tree-container
        .p-treenode
        .p-treenode-content.p-highlight
        .p-tree-toggler {
        color: #798da9;
      }
    `,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ListingsPickerComponent),
      multi: true,
    },
  ],
})
export class ListingsPickerComponent implements OnInit, ControlValueAccessor {
  public loading: boolean;
  searchDebounce: any;
  destinationNode: TreeNode<Listing | Category>;
  searchKeywords: string = "";
  searchResults: Listing[] = [];
  searchResultsSelection: Listing[] = [];
  selectedStatuses: string[] = [];
  selectedStocks: string[] = [];
  statuses: { key: string; label: string }[] = [
    { key: "active", label: $localize`Active` },
    { key: "inactive", label: $localize`Inactive` },
  ];
  stocks: { key: string; label: string }[] = [
    { key: "available", label: $localize`Available` },
    { key: "out_of_stock", label: $localize`Out of Stock` },
  ];

  categories: Category[] = [];

  _value: TreeNode<Listing | Category>[];
  _valueMap: { [key: string]: TreeNode<Listing | Category> } = {};
  _filteredValue: TreeNode<Listing | Category>[];
  _filteredValueMap: { [key: string]: TreeNode<Listing | Category> } = {};
  _selection: TreeNode<Listing | Category>[];

  _checkAll: boolean;
  showFilters: boolean;

  @Output()
  selectionChange = new EventEmitter<TreeNode<Listing | Category>[]>();

  initializing: boolean = false;

  private loadChildrenQueue: TreeNode<Listing | Category>[] = [];
  public isLoadingChildren: boolean = false;

  @Output()
  childrenLoaded = new EventEmitter<TreeNode<Listing | Category>>();

  constructor(
    private remoteDataService: RemoteDataService,
    private messageService: MessageService
  ) {}

  async ngOnInit() {
    this.initializing = true;
    const user = await this.remoteDataService.get<User>(USER_API_URL);
    const globalSettings = await this.remoteDataService.query<GlobalSettings>(
      GLOBAL_SETTINGS_API_URL
    );
    const platformStatuses = _.find(globalSettings, {
      key: "platform_statuses",
    });
    this.statuses = platformStatuses.parameters[user.accounts[0].type];
    this.selectedStatuses = this.statuses.map((status) => status.key);
    this.selectedStocks = this.stocks.map((stock) => stock.key);
    this.loadCategories();
  }
  async loadCategories() {
    this.value = _.map(
      await this.remoteDataService.query<Category>(CATEGORY_API_URL),
      (category) => {
        category.title = category.title || category.name;
        const node = {
          key: category.id,
          data: category,
          kind: "category",
          leaf: false,
          children: [],
          loading: false,
        };
        return node;
      }
    );
    this.filteredValue = this.value;
    this.initializing = false;
  }
  async searchListings() {
    if (this.searchDebounce) {
      clearTimeout(this.searchDebounce);
    }
    this.searchDebounce = setTimeout(async () => {
      const params = {
        keywords: this.searchKeywords,
        limit: 100,
      };
      this.loading = true;
      const newSearchResults = await this.remoteDataService.query<Listing>(
        `${LISTING_API_URL}`,
        params
      );
      this.searchResults = newSearchResults; // Only keep the last search results
      this.loading = false;
    }, 500); // 300ms debounce
  }

  clearSearch() {
    this.searchKeywords = "";
    this.searchResults = [];
  }

  async loadChildren(parentNode: TreeNode<Listing | Category>) {
    if (this.isLoadingChildren) {
      // If already loading, add to queue
      this.loadChildrenQueue.push(parentNode);
      return;
    }

    try {
      if (
        !parentNode.leaf &&
        (!this.getFilteredNode(parentNode.key).children ||
          this.getFilteredNode(parentNode.key).children?.length === 0)
      ) {
        this.isLoadingChildren = true;
        await this.processLoadChildren(parentNode);
      }
    } finally {
      this.isLoadingChildren = false;

      // Process next item in queue if any
      if (this.loadChildrenQueue.length > 0) {
        const nextNode = this.loadChildrenQueue.shift();
        this.loadChildren(nextNode);
      }
    }
  }

  private async processLoadChildren(parentNode: TreeNode<Listing | Category>) {
    if (parentNode.children?.length > 0 || parentNode.leaf) {
      return;
    }

    const node = this.getNode(parentNode.key);
    const params = {
      parent: node.data.id,
    };

    this.setNodeValue(node.key, { loading: true });
    this.loading = true;

    try {
      await this.remoteDataService.query(`${LISTING_API_URL}/bulk`, params);
      let taskStatus = { is_running: true };
      while (taskStatus.is_running) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        taskStatus = await this.remoteDataService.get(
          `${LISTING_API_URL}/bulk/status`
        );
      }
      const listings = await this.remoteDataService.get<Listing>(
        `${LISTING_API_URL}/bulk/result`
      );

      this.setChildren(
        node.key,
        _.map(listings, (listing) => ({
          key: listing.id,
          parent: node,
          kind: "listing",
          data: listing,
          leaf: true,
        }))
      );
      this.setNodeValue(node.key, { leaf: node.children.length === 0 });
    } finally {
      this.loading = false;
      this.setNodeValue(node.key, { loading: false, loaded: true });
    }
    this.refreshValueMap();
  }

  async onNodeExpand(event) {
    this.loadChildren(event.node);
  }

  // set checkAll(value: boolean) {
  //   this._checkAll = value;

  //   if (value) {
  //     this.nodes.selection = [...this.nodes.value];
  //   } else {
  //     this.nodes.selection = [];
  //   }
  //   this.onChange(this.nodes.selection); // Notify change
  // }

  // get checkAll(): boolean {
  //   return this._checkAll;
  // }

  setNodeValue(key: string, value: Record<string, any>) {
    const node = this.getNode(key);
    const filteredNode = this.getFilteredNode(key);

    Object.assign(node, value);
    Object.assign(filteredNode, value);
  }

  getNode(key: string): TreeNode<Listing | Category> {
    return this._valueMap[key];
  }

  getFilteredNode(key: string): TreeNode<Listing | Category> {
    return this._filteredValueMap[key];
  }

  matchFilters(node: TreeNode<Listing | Category>): boolean {
    const listing = node.data as Listing;
    return (
      (this.selectedStatuses.length === 0 ||
        this.selectedStatuses.includes(listing.status)) &&
      ((this.selectedStocks.includes("available") && listing.in_stock) ||
        (this.selectedStocks.includes("out_of_stock") && !listing.in_stock))
    );
  }

  setChildren(key: string, children: TreeNode<Listing | Category>[]) {
    this.getNode(key).children = children;
    this.getFilteredNode(key).children = children;
    this.refreshFilteredValue();
    this.childrenLoaded.emit(this.getFilteredNode(key));
  }

  isSelected(key: string): boolean {
    return (
      this.selection.some((selectedNode) => selectedNode.key === key) ||
      this.searchResultsSelection.some(
        (selectedNode) => selectedNode.id === key
      )
    );
  }

  onSearchResultSelectionChange() {
    const UNCATEGORIZED_ID = "0";
    let categoryNode = this.getNode(UNCATEGORIZED_ID);
    if (!categoryNode) {
      categoryNode = <any>{
        key: UNCATEGORIZED_ID,
        data: {
          id: "0",
          title: $localize`Uncategorized`,
          isCustomCollection: true,
        },
        kind: "category",
        leaf: false,
        children: [],
        loading: false,
        loaded: true,
      };
      this.value = [...this.value, categoryNode];
      this.filteredValue = this.value;
    }
    const children = categoryNode.children;
    for (const searchResult of this.searchResultsSelection) {
      const childrenNode = <any>{
        key: searchResult.id,
        parent: this.getNode(UNCATEGORIZED_ID),
        kind: "listing",
        data: searchResult,
        leaf: true,
      };
      if (!children.some((childNode) => childNode.key === searchResult.id)) {
        children.push(childrenNode);
      }
      if (
        !this.selection.some(
          (selectedNode) => selectedNode.key === searchResult.id
        )
      ) {
        this.selection = <any>[...this.selection, childrenNode];
      }
    }
    this.setChildren(UNCATEGORIZED_ID, children);
    this.refreshValueMap();
    this.refreshSelection();
    this.onChange(this.selection);
  }

  filtersChanged() {
    this.refreshFilteredValue();
  }

  refreshFilteredValue() {
    const filteredValue = [];
    for (const parentNode of this.value) {
      let existingFilteredNode = this.getFilteredNode(parentNode.key);
      const filteredNode = {
        ...parentNode,
        ...existingFilteredNode,
        children: [],
      };
      filteredValue.push(filteredNode);
      for (const childNode of parentNode.children) {
        if (this.matchFilters(childNode)) {
          existingFilteredNode = this.getFilteredNode(childNode.key);
          filteredNode.children.push({
            ...childNode,
            ...existingFilteredNode,
          });
        }
      }
    }
    this.filteredValue = filteredValue;
    this.refreshSelection();
  }

  refreshSelection() {
    const selection = [];
    for (const parentNode of this.filteredValue) {
      if (this.isSelected(parentNode.key)) {
        selection.push(parentNode);
        selection.push(...parentNode.children);
      }
      for (const childNode of parentNode.children) {
        if (this.isSelected(childNode.key)) {
          selection.push(childNode);
        }
      }
    }
    this.selection = selection;
  }

  set filteredValue(value: TreeNode<Listing | Category>[]) {
    this._filteredValue = value;
    this._filteredValueMap = {};
    this._filteredValue.forEach((node) => {
      this._filteredValueMap[node.key] = node;

      for (const childNode of node.children) {
        this._filteredValueMap[childNode.key] = childNode;
      }
    });
  }

  set value(value: TreeNode<Listing | Category>[]) {
    this._value = value;
    this.refreshValueMap();
  }

  refreshValueMap() {
    this._valueMap = {};
    this._value.forEach((node) => {
      this._valueMap[node.key] = node;

      for (const childNode of node.children) {
        this._valueMap[childNode.key] = childNode;
      }
    });
  }

  set selection(value: TreeNode<Listing | Category>[]) {
    this._selection = value;
  }

  get filteredValue(): TreeNode<Listing | Category>[] {
    return this._filteredValue;
  }

  get value(): TreeNode<Listing | Category>[] {
    return this._value;
  }

  get selection(): TreeNode<Listing | Category>[] {
    return this._selection;
  }

  async onSelectionChange(event) {
    for (const node of event) {
      if (node.kind === "category") {
        this.loadChildren(node);
      }
    }
    this.refreshSelection();
    console.log(this.selection);
    const selection = _.map(this.selection, (node) => this.getNode(node.key));
    console.log(selection);
    this.onChange(selection);
  }

  // ControlValueAccessor methods
  writeValue(value: TreeNode<Listing | Category>[]): void {
    if (value) {
      this.selection = value;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  selectAll() {
    this.selection = this.filteredValue;
    this.onSelectionChange(this.selection);
  }

  deselectAll() {
    this.selection = [];
    this.onSelectionChange(this.selection);
  }

  private onChange: any = () => {};
  private onTouched: any = () => {};
  private isDisabled: boolean = false;
}
